import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import i18n from '../../i18n';
import axiosInstance from '../api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const PROP_BADGE = {
  AUTO_SATISFIED: { label: i18n.t('ComplianceHub.badgeAutoSatisfied'), bg: '#dcfce7', color: '#166534' },
  PENDING_REVIEW: { label: i18n.t('ComplianceHub.badgePendingReview'), bg: '#fef9c3', color: '#854d0e' },
  CONFIRMED:      { label: i18n.t('ComplianceHub.badgeConfirmed'),     bg: '#dbeafe', color: '#1e40af' },
  REJECTED:       { label: i18n.t('ComplianceHub.badgeRejected'),      bg: '#fee2e2', color: '#991b1b' },
};

// The opportunity endpoint historically shipped `coveragePercent`; older callers read
// `coveragePct`. Read either so the hero metric can never silently render as 0.
const coverageOf = (o) => Math.round(o.coveragePercent ?? o.coveragePct ?? 0);
const reusedOf   = (o) => (o.alreadySatisfied || 0) + (o.mappedCoverage || 0);

function PctBar({ value, color = '#6366f1', height = 8 }) {
  return (
    <div style={{ background: '#e5e7eb', borderRadius: 4, height, overflow: 'hidden', flex: 1 }}>
      <div style={{ width: `${Math.min(100, value || 0)}%`, background: color, height: '100%', transition: 'width .4s' }} />
    </div>
  );
}

// The /compliance/frameworks payload carries the pct fields at the TOP level (and
// frameworkId/frameworkName). Read from fw.progress OR fw directly, and resolve id/name from
// either shape — otherwise every posture card silently renders 0% (the bug that made the Hub
// disagree with the Audits page for the same org).
function FrameworkCard({ fw, onSelect, selected }) {
  const p = fw.progress || fw;
  const fwId = fw.id ?? fw.frameworkId;
  const fwName = fw.displayName || fw.frameworkName || fw.name;
  return (
    <div
      onClick={() => onSelect(fwId)}
      style={{
        border: `2px solid ${selected ? '#6366f1' : '#e5e7eb'}`,
        borderRadius: 10, padding: 16, cursor: 'pointer',
        background: selected ? '#f5f3ff' : '#fff',
        transition: 'all .2s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{fwName}</span>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
          background: p.compliantPct >= 80 ? '#dcfce7' : p.compliantPct >= 50 ? '#fef9c3' : '#fee2e2',
          color:      p.compliantPct >= 80 ? '#166534' : p.compliantPct >= 50 ? '#854d0e' : '#991b1b',
        }}>
          {Math.round(p.compliantPct || 0)}%
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: '#6b7280', width: 110 }}>{i18n.t('ComplianceHub.compliant')}</span>
        <PctBar value={p.compliantPct} color="#16a34a" />
        <span style={{ fontSize: 11, width: 30 }}>{Math.round(p.compliantPct || 0)}%</span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: '#6b7280', width: 110 }}>{i18n.t('ComplianceHub.autoSatisfied')}</span>
        <PctBar value={p.autoSatisfiedPct} color="#6366f1" />
        <span style={{ fontSize: 11, width: 30 }}>{Math.round(p.autoSatisfiedPct || 0)}%</span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: '#6b7280', width: 110 }}>{i18n.t('ComplianceHub.failing')}</span>
        <PctBar value={p.failingPct} color="#dc2626" />
        <span style={{ fontSize: 11, width: 30 }}>{Math.round(p.failingPct || 0)}%</span>
      </div>
      {p.autoSatisfiedPct > 0 && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#6366f1' }}>
          <i className="fas fa-magic" style={{ marginRight: 4 }} />
          {i18n.t('ComplianceHub.crossFrameworkPropagationActive')}
        </div>
      )}
    </div>
  );
}

// HERO — the reason a buyer stays on one platform: work done for one framework is already
// paying down the next. Headlines the REALIZED reuse from the (fast, populated) posture data —
// controls auto-satisfied across frameworks via the mapping library — and keeps a prospective
// "adopt with reuse" nudge for not-yet-covered frameworks when the opportunity data is available.
function CoverageHero({ t, frameworks, opportunities, opportunitiesFailed, onAdopt }) {
  // Realized cross-framework reuse, straight from each framework's auto-satisfied count.
  const reuse = (frameworks || [])
    .map(fw => ({
      id:     fw.frameworkId ?? fw.id,
      name:   fw.frameworkName || fw.displayName || fw.name,
      auto:   Number(fw.autoSatisfied || 0),
      autoPct: Math.round(fw.autoSatisfiedPct || 0),
      total:  Number(fw.totalControls || 0),
    }))
    .sort((a, b) => b.auto - a.auto);

  const totalReused = reuse.reduce((n, f) => n + f.auto, 0);
  const frameworksTouched = reuse.filter(f => f.auto > 0).length;
  const benefiting = reuse.filter(f => f.auto > 0).slice(0, 3);

  // UPSELL — the point of this page: frameworks the org has NOT adopted, but whose controls are
  // already substantially covered by the current posture (shared controls + mappings). Sorted by
  // how ready they are, so the org sees "you're 78% of the way to ITIL — adopt with little effort".
  const upsell = (opportunities || [])
    .map(o => ({ ...o, _cov: coverageOf(o), _reused: reusedOf(o) }))
    .filter(o => o.enrolled === false && (o.totalControls || 0) > 0 && o._cov > 0)
    .sort((a, b) => b._cov - a._cov).slice(0, 4);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%)',
      border: '1px solid #e0e7ff', borderRadius: 14, padding: 22, marginBottom: 22,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <i className="fas fa-recycle" style={{ color: '#6366f1', fontSize: 18 }} />
        <span style={{ fontSize: 16, fontWeight: 700, color: '#3730a3' }}>
          {t('ComplianceHub.heroTitle', 'Cross-framework coverage')}
        </span>
      </div>
      <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 16 }}>
        {totalReused > 0
          ? t('ComplianceHub.heroLede', {
              defaultValue:
                'Your passing controls already auto-satisfy {{count}} controls across {{frameworks}} framework(s) — evidence earned once, reused everywhere.',
              count: totalReused, frameworks: frameworksTouched,
            })
          : t('ComplianceHub.heroEmpty',
              'As you pass controls, Cataam maps them to every other framework so adopting the next one is mostly done for you.')}
      </div>

      {benefiting.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
          {benefiting.map((f) => (
            <div key={f.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{f.name}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: '#6366f1' }}>{f.autoPct}%</span>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{t('ComplianceHub.autoSatisfiedViaReuse', 'auto-satisfied via reuse')}</span>
              </div>
              <PctBar value={f.autoPct} color="#6366f1" height={10} />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
                {t('ComplianceHub.reuseCount', {
                  defaultValue: '{{auto}} of {{total}} controls satisfied from other frameworks',
                  auto: f.auto, total: f.total,
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {upsell.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#3730a3', marginBottom: 10 }}>
            <i className="fas fa-arrow-trend-up" style={{ marginRight: 8 }} />
            {t('ComplianceHub.upsellTitle', 'Adopt these with little extra effort — your current controls already cover them')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
          {upsell.map((o) => (
            <div key={`opp-${o.frameworkId}`} style={{ background: '#fff', border: '1px dashed #c7d2fe', borderRadius: 10, padding: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{o.frameworkName}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: '#6366f1' }}>{o._cov}%</span>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{t('ComplianceHub.alreadyCovered', 'already covered')}</span>
              </div>
              <PctBar value={o._cov} color="#6366f1" height={10} />
              <div style={{ fontSize: 11, color: '#6b7280', margin: '8px 0 12px' }}>
                {t('ComplianceHub.upsellBreakdown', {
                  defaultValue: '{{covered}} of {{total}} controls already met · {{net}} net-new',
                  covered: (o.alreadySatisfied || 0) + (o.mappedCoverage || 0), net: o.netNewControls || 0, total: o.totalControls || 0,
                })}
              </div>
              <button
                onClick={() => onAdopt(o)}
                style={{
                  width: '100%', padding: '8px 12px', background: '#6366f1', color: '#fff',
                  border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}>
                <i className="fas fa-bolt" style={{ marginRight: 6 }} />
                {t('ComplianceHub.adoptWithReuse', 'Adopt with reuse')}
              </button>
            </div>
          ))}
          </div>
        </div>
      )}

      {upsell.length === 0 && opportunitiesFailed && (
        <div style={{ marginTop: 18, fontSize: 12, color: '#b91c1c' }}>
          {t('ComplianceHub.upsellFailed', "Couldn't load cross-framework recommendations — refresh to retry.")}
        </div>
      )}
    </div>
  );
}

export default function ComplianceHub() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [frameworks, setFrameworks]     = useState([]);
  const [opportunity, setOpportunity]   = useState([]);
  const [opportunityFailed, setOpportunityFailed] = useState(false);
  const [detail, setDetail]             = useState(null);
  const [selectedFw, setSelectedFw]     = useState(null);
  const [loadingFw, setLoadingFw]       = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError]               = useState('');
  const [actionMsg, setActionMsg]       = useState('');
  const [tab, setTab]                   = useState('posture');

  const load = useCallback(async () => {
    setLoadingFw(true);
    try {
      const fwRes = await axiosInstance.get('/compliance/frameworks', { headers: authHeaders() });
      setFrameworks(fwRes.data || []);
    } catch {
      setError(t('ComplianceHub.failedToLoad'));
    } finally {
      setLoadingFw(false);
    }
    // Cross-framework opportunity powers the hero but can take 10s+ on large orgs — load it
    // separately so the framework-posture grid renders immediately instead of blocking on it.
    setOpportunityFailed(false);
    axiosInstance.get('/compliance/cross-framework-opportunity', { headers: authHeaders() })
      .then((r) => setOpportunity(r.data || []))
      .catch(() => setOpportunityFailed(true));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id) => {
    setLoadingDetail(true);
    setSelectedFw(id);
    try {
      const res = await axiosInstance.get(`/compliance/frameworks/${id}/progress`, { headers: authHeaders() });
      setDetail(res.data);
    } catch {
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const act = async (id, action) => {
    setActionMsg('');
    try {
      await axiosInstance.post(`/compliance/audit-progress/${id}/${action}`, {}, { headers: authHeaders() });
      setActionMsg(action === 'confirm' ? t('ComplianceHub.controlConfirmedSuccess') : t('ComplianceHub.controlRejectedSuccess'));
      if (selectedFw) loadDetail(selectedFw);
    } catch {
      setActionMsg(t('ComplianceHub.actionFailed'));
    }
  };

  // Reuse CTA → the Frameworks catalog, pre-targeting the framework to adopt. Signup itself
  // (and its subscription wall) stays on the Frameworks page; the Hub only routes the intent.
  const adopt = (opp) => {
    navigate('/dashboard/compliance/frameworks', { state: { adoptFrameworkId: opp.frameworkId } });
  };

  const s = {
    page:    { padding: 24, fontFamily: 'Inter, sans-serif', maxWidth: 1200 },
    h1:      { fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4 },
    sub:     { fontSize: 13, color: '#6b7280', marginBottom: 20 },
    tabs:    { display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 20 },
    tab:     (a) => ({ padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
                       fontWeight: 600, fontSize: 13, color: a ? '#6366f1' : '#6b7280',
                       borderBottom: a ? '2px solid #6366f1' : '2px solid transparent', marginBottom: -2 }),
    grid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 },
    card:    { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 },
    msg:     { padding: '8px 14px', borderRadius: 6, fontSize: 13, marginBottom: 12,
                background: '#dbeafe', color: '#1e40af' },
  };

  if (loadingFw) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /> {t('ComplianceHub.loading')}</div>;
  if (error)     return <div style={{ padding: 20, color: '#dc2626' }}>{error}</div>;

  const pendingCount = (detail?.auditItems || []).filter(i => i.propagationStatus === 'PENDING_REVIEW').length;

  return (
    <div style={s.page}>
      <div style={s.h1}>{t('ComplianceHub.title')}</div>
      <div style={s.sub}>
        {t('ComplianceHub.subtitleReuse',
           'One control, every framework. See how coverage you have already earned pays down the frameworks you have not adopted yet.')}
      </div>

      {actionMsg && <div style={s.msg}>{actionMsg}</div>}

      {/* ── HERO: cross-framework coverage (the primary story) ─────────── */}
      <CoverageHero t={t} frameworks={frameworks} opportunities={opportunity} opportunitiesFailed={opportunityFailed} onAdopt={adopt} />

      <div style={s.tabs}>
        <button style={s.tab(tab === 'posture')} onClick={() => setTab('posture')}>
          {t('ComplianceHub.tabFrameworkPosture', 'Framework posture')}
        </button>
        <button style={s.tab(tab === 'queue')} onClick={() => setTab('queue')}>
          {t('ComplianceHub.tabReuseQueue', 'Reuse queue')}{pendingCount ? ` (${pendingCount})` : ''}
        </button>
      </div>

      {/* ── Framework posture ────────────────────────────────────────── */}
      {tab === 'posture' && (
        <div style={{ display: 'grid', gridTemplateColumns: selectedFw ? '1fr 1fr' : '1fr', gap: 20 }}>
          <div>
            <div style={s.grid}>
              {frameworks.map(fw => {
                const fwId = fw.id ?? fw.frameworkId;
                return (
                  <FrameworkCard
                    key={fwId}
                    fw={fw}
                    selected={selectedFw === fwId}
                    onSelect={loadDetail}
                  />
                );
              })}
            </div>
          </div>

          {selectedFw && (
            <div style={s.card}>
              {loadingDetail ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>
              ) : detail ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ fontWeight: 700, fontSize: 16 }}>{detail.frameworkName}</span>
                    <button onClick={() => { setSelectedFw(null); setDetail(null); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 16 }}>
                      <i className="fas fa-times" />
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                    {['compliantPct', 'autoSatisfiedPct', 'failingPct', 'notStartedPct'].map(k => (
                      <div key={k} style={{ flex: '1 1 100px', background: '#f9fafb', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>{Math.round(detail[k] || 0)}%</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>
                          {k === 'compliantPct'     && t('ComplianceHub.compliant')}
                          {k === 'autoSatisfiedPct' && t('ComplianceHub.autoSatisfied')}
                          {k === 'failingPct'       && t('ComplianceHub.failing')}
                          {k === 'notStartedPct'    && t('ComplianceHub.notStarted')}
                        </div>
                      </div>
                    ))}
                  </div>
                  {(detail.auditItems || []).length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left' }}>{t('ComplianceHub.columnControl')}</th>
                          <th style={{ padding: '6px 10px', textAlign: 'left' }}>{t('ComplianceHub.columnStatus')}</th>
                          <th style={{ padding: '6px 10px', textAlign: 'left' }}>{t('ComplianceHub.columnPropagation')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.auditItems.map(item => {
                          const propB = PROP_BADGE[item.propagationStatus];
                          return (
                            <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '7px 10px' }}>{item.controlName || item.controlId}</td>
                              <td style={{ padding: '7px 10px' }}>
                                <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10,
                                  background: item.status === 'COMPLIANT' ? '#dcfce7' : item.status === 'FAILING' ? '#fee2e2' : '#f3f4f6',
                                  color:      item.status === 'COMPLIANT' ? '#166534' : item.status === 'FAILING' ? '#991b1b' : '#374151' }}>
                                  {item.status}
                                </span>
                              </td>
                              <td style={{ padding: '7px 10px' }}>
                                {propB ? (
                                  <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: propB.bg, color: propB.color }}>
                                    {propB.label}
                                  </span>
                                ) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </>
              ) : (
                <div style={{ color: '#6b7280', textAlign: 'center', padding: 20 }}>{t('ComplianceHub.couldNotLoadDetail')}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Reuse queue (confirm/reject auto-propagated controls) ─────── */}
      {tab === 'queue' && (
        <div style={s.card}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('ComplianceHub.controlsPendingCpaReview')}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
            {t('ComplianceHub.queueHint', 'Select a framework in Framework posture to review the controls auto-satisfied via cross-framework mapping.')}
          </div>
          {pendingCount === 0 ? (
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              {t('ComplianceHub.pendingEmptyState')}
            </div>
          ) : null}
          {selectedFw && detail && (detail.auditItems || []).filter(i => i.propagationStatus === 'PENDING_REVIEW').map(item => (
            <div key={item.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{item.controlName || t('ComplianceHub.controlNumber', { id: item.controlId })}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => act(item.id, 'confirm')}
                    style={{ padding: '5px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                    {t('ComplianceHub.confirm')}
                  </button>
                  <button
                    onClick={() => act(item.id, 'reject')}
                    style={{ padding: '5px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                    {t('ComplianceHub.reject')}
                  </button>
                </div>
              </div>
              {item.propagatedFromControlId && (
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  {t('ComplianceHub.propagatedFromControl', { id: item.propagatedFromControlId })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
